import type { WorkspaceMode } from './workspace';

export enum ClusterStatus {
  DRAFT = 'draft',
  IN_PROGRESS = 'in_progress',
  COMPLETE = 'complete',
  VALIDATED = 'validated',
  HAS_GAPS = 'has_gaps',
  APPROVED = 'approved',
}

export interface Cluster {
  cluster_id: string;
  workspace_id: string;
  name: string;
  summary: string | null;
  purpose: string | null;
  status: ClusterStatus;
  parent_cluster_id: string | null;
  model_id: string | null;
  member_node_ids: string[];
  tags: string[];
  owner: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClusterVocab {
  singular: string;
  plural: string;
}

export const CLUSTER_VOCAB: Record<WorkspaceMode, ClusterVocab> = {
  research: { singular: 'topic', plural: 'topics' },
  production: { singular: 'module', plural: 'modules' },
  plan: { singular: 'section', plural: 'sections' },
  institution_context: { singular: 'cluster', plural: 'clusters' },
  // documentation has no logic-node graph to cluster; entry exists only to keep
  // the map exhaustive over WorkspaceMode.
  documentation: { singular: 'document', plural: 'documents' },
  output_package: { singular: 'artifact', plural: 'artifacts' },
};

export const CLUSTER_STATUS_CSS_VARS: Record<ClusterStatus, string> = {
  [ClusterStatus.DRAFT]: '--on-surface-tertiary',
  [ClusterStatus.IN_PROGRESS]: '--warning',
  [ClusterStatus.COMPLETE]: '--success',
  [ClusterStatus.VALIDATED]: '--info',
  [ClusterStatus.HAS_GAPS]: '--danger',
  [ClusterStatus.APPROVED]: '--success',
};

const CLUSTER_STATUS_VALUES = new Set<string>(Object.values(ClusterStatus));

function nullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

export function narrowCluster(raw: unknown): Cluster {
  const source = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {};
  const status = typeof source.status === 'string' && CLUSTER_STATUS_VALUES.has(source.status)
    ? source.status as ClusterStatus
    : ClusterStatus.DRAFT;

  return {
    cluster_id: typeof source.cluster_id === 'string' ? source.cluster_id : '',
    workspace_id: typeof source.workspace_id === 'string' ? source.workspace_id : '',
    name: typeof source.name === 'string' ? source.name : 'Untitled cluster',
    summary: nullableString(source.summary),
    purpose: nullableString(source.purpose),
    status,
    parent_cluster_id: nullableString(source.parent_cluster_id),
    model_id: nullableString(source.model_id),
    member_node_ids: stringArray(source.member_node_ids),
    tags: stringArray(source.tags),
    owner: nullableString(source.owner),
    created_at: typeof source.created_at === 'string' ? source.created_at : '',
    updated_at: typeof source.updated_at === 'string' ? source.updated_at : '',
  };
}

export function narrowClusterRecord(raw: unknown): Record<string, Cluster> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }

  return Object.entries(raw as Record<string, unknown>).reduce<Record<string, Cluster>>((acc, [key, value]) => {
    const cluster = narrowCluster(value);
    const clusterId = cluster.cluster_id || key;
    acc[clusterId] = { ...cluster, cluster_id: clusterId };
    return acc;
  }, {});
}
